import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import useAuth from "../hooks/useAuth";
import { useNotificationSnack } from "../hooks/useNotificationSnack";
import authServices from "../services/authServices";
import { createEcho } from "../utils/echo";

const NotificationContext = createContext();
export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const user = useAuth();
  const echoRef = useRef(null);
  const { showNotificationSnack } = useNotificationSnack();

  const fetchNotifications = async () => {
    try {
      const response = await authServices.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch Notifications:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await authServices.getNotificationsNotReadCount();
      setUnreadCount(response.count);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      await authServices.readNotifications(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  useEffect(() => {
    if (!user?.id || !user?.access_token) return;

    fetchNotifications();
    fetchUnreadCount();

    if (echoRef.current) {
      echoRef.current.disconnect();
    }

    const echo = createEcho(user.access_token);
    echoRef.current = echo;

    echo.connector.pusher.connection.bind("connected", () => {
      console.log("Connected to WebSocket");
    });

    echo.connector.pusher.connection.bind("error", (err) => {
      console.error("WebSocket error:", err);
    });

    echo.private(`App.Models.User.${user.id}`).notification((notification) => {
      console.log("Received private notification", notification);
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    echo.channel("public.notifications").listen(".PublicNotificationEvent", (event) => {
      console.log("Received public event", event);
      setNotifications((prev) => [event, ...prev]);
      showNotificationSnack(event);
      setUnreadCount((prev) => prev + 1);
    });

    return () => {
      if (echoRef.current) {
        echoRef.current.leave(`App.Models.User.${user.id}`);
        echoRef.current.leaveChannel("public.notifications");
        echoRef.current.disconnect();
      }
    };
  }, [user?.id, user?.access_token]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        refreshUnreadCount: fetchUnreadCount,
        markNotificationAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
